import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
    useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIdleTimer } from "react-idle-timer";
import { withAuth0 } from "@auth0/auth0-react";
import PropTypes from "prop-types";

import {
    handleLogout as handleLogoutAction,
    setAuthToken,
    setUserData,
} from "../Login/actions";
import {
    acquirePlanLock,
    checkPlanLocked,
    checkSharedPatient,
    releasePlanLock,
} from "../../helpers/backend_api";
import {
    stepPath,
    SUCCESS,
    BROADCAST_CHANNEL_NAME,
    BROADCAST_CHANNEL_LOGOUT_MSG,
} from "../../constants";
import { getUser } from "../Login/helpers";
import SessionExpiryModal from "./SessionExpiryModal";
import { clearPendoSession } from "../../helpers/helpers";
import { setSurgeryMode } from "../../redux/surgery_mode/actions";

const MS_PER_MINUTE = 60 * 1000;
const SNOOZE_DURATION_MS = 60 * MS_PER_MINUTE;
const SNOOZE_RENEW_INTERVAL_MS = 5 * MS_PER_MINUTE;
const COUNTDOWN_INTERVAL_MS = 1000;
const INITIAL_PROMPT_SECONDS = 60;
const SESSION_TIMEOUT_MINUTES_KEY = "sessionTimeoutMinutes";
const USER_DATA_REFRESH_INTERVAL_MS = 14 * MS_PER_MINUTE;
const SURGERY_MODE_MAX_MS = 8 * 60 * MS_PER_MINUTE;

function SessionManager(props) {
    const { auth0, debounceDuration, timeoutDuration, promptDuration } = props;

    const dispatch = useDispatch();

    const unText = useSelector((state) => state.LoginReducer.unText);
    const authToken = useSelector((state) => state.LoginReducer.authToken);
    const userUuid = useSelector((state) => state.LoginReducer.useruuid);
    const planUuid = useSelector((state) => state.CreatePlanReducer.planuuid);
    const patientUuid = useSelector(
        (state) => state.CreatePlanReducer.patientuuid
    );
    const autoLogoutPaused = useSelector(
        (state) => state.SurgeryModeReducer.autoLogoutPaused
    );

    const dispatchHandleLogout = useCallback(
        () => dispatch(handleLogoutAction()),
        [dispatch]
    );
    const setAuthTokenAction = useCallback(
        (token) => dispatch(setAuthToken(token)),
        [dispatch]
    );

    const debounceMs = MS_PER_MINUTE * debounceDuration;

    // check localStorage for timeout
    // if none, use 15 mins default
    const timeoutMinutes = useMemo(() => {
        const persisted = parseInt(
            window.localStorage.getItem(SESSION_TIMEOUT_MINUTES_KEY)
        );
        return Number.isFinite(persisted) && persisted > 0
            ? persisted
            : timeoutDuration;
    }, [timeoutDuration]);

    const totalTimeoutMs = MS_PER_MINUTE * timeoutMinutes;

    // when to show warning
    const promptMs = Math.max(
        0,
        Math.min(promptDuration * MS_PER_MINUTE, totalTimeoutMs - 1000)
    );

    const [showWarning, setShowWarning] = useState(false);
    const [promptRemainingSeconds, setPromptRemainingSeconds] = useState(
        INITIAL_PROMPT_SECONDS
    );

    const promptIntervalRef = useRef(null);
    const logoutBroadcastChannelRef = useRef(null);
    const snoozeTimeoutRef = useRef(null);
    const snoozeRenewIntervalRef = useRef(null);
    const originalTitleRef = useRef(null);
    const userDataRefreshIntervalRef = useRef(null);

    // cleanup functions
    const clearPromptInterval = useCallback(() => {
        if (promptIntervalRef.current) {
            clearInterval(promptIntervalRef.current);
            promptIntervalRef.current = null;
        }
    }, []);

    const restoreOriginalTitle = useCallback(() => {
        if (originalTitleRef.current) {
            document.title = originalTitleRef.current;
        }
    }, []);

    const clearSnoozeTimers = useCallback(() => {
        if (snoozeTimeoutRef.current) {
            clearTimeout(snoozeTimeoutRef.current);
            snoozeTimeoutRef.current = null;
        }
        if (snoozeRenewIntervalRef.current) {
            clearInterval(snoozeRenewIntervalRef.current);
            snoozeRenewIntervalRef.current = null;
        }
    }, []);

    const clearUserDataRefreshInterval = useCallback(() => {
        if (userDataRefreshIntervalRef.current) {
            clearInterval(userDataRefreshIntervalRef.current);
            userDataRefreshIntervalRef.current = null;
        }
    }, []);

    const clearUiState = useCallback(() => {
        clearPromptInterval();
        clearSnoozeTimers();
        restoreOriginalTitle();
        clearUserDataRefreshInterval();
    }, [
        clearPromptInterval,
        clearSnoozeTimers,
        restoreOriginalTitle,
        clearUserDataRefreshInterval,
    ]);

    const handleLogout = useCallback(async () => {
        // 1. Release plan lock if we have one
        if (planUuid) {
            await releasePlanLock({
                token: authToken,
                useruuid: userUuid,
                planuuid: planUuid,
            });
        }

        // 2. Clear Pendo session if available
        clearPendoSession();

        // 3. Clear Redux state
        dispatchHandleLogout();

        // 4. Log out other tabs
        try {
            logoutBroadcastChannelRef.current?.postMessage(
                BROADCAST_CHANNEL_LOGOUT_MSG
            );
        } catch {
            // no-op
        }

        // 5. Redirect to login
        auth0.logout({
            logoutParams: {
                returnTo: window.location.origin + stepPath.LOGIN,
            },
        });
    }, [authToken, userUuid, planUuid, dispatchHandleLogout, auth0]);

    const renewToken = useCallback(async () => {
        try {
            const tokenValid = await auth0.getAccessTokenSilently();
            setAuthTokenAction(tokenValid);
        } catch {
            await handleLogout();
        }
    }, [auth0, setAuthTokenAction, handleLogout]);

    // Fetches updated user data every 14 minutes
    const refreshUserData = useCallback(() => {
        if (!unText || !authToken) {
            return;
        }

        getUser(unText, authToken).then((payload) => {
            if (payload && payload.status === SUCCESS) {
                dispatch(setUserData(payload.user));
            }
        });
    }, [unText, authToken, dispatch]);

    const onAction = useCallback(() => {
        // Validate authentication state
        if (!unText || !auth0.isAuthenticated || !sessionStorage.authToken) {
            return;
        }

        // 1. Renew token
        renewToken().then(async () => {
            if (!patientUuid || !planUuid) {
                return;
            }

            // 2. Check if we still have access to this patient
            const checkSharedPatientResponse = await checkSharedPatient({
                patientuuid: patientUuid,
                token: authToken,
            });
            if (!checkSharedPatientResponse) return;

            // 3. Check if the plan is locked by someone else
            const checkPlanLockedResponse = await checkPlanLocked({
                token: authToken,
                planuuid: planUuid,
            });

            // 4. Try to acquire the lock if available
            const canAcquireLock =
                checkPlanLockedResponse.status === SUCCESS &&
                (!checkPlanLockedResponse.planLocked ||
                    checkPlanLockedResponse.lockHolderUserUuid === userUuid);

            if (canAcquireLock) {
                await acquirePlanLock({
                    token: authToken,
                    planuuid: planUuid,
                    useruuid: userUuid,
                });
            }
        });
    }, [unText, auth0, patientUuid, authToken, planUuid, userUuid, renewToken]);

    // Trigger logout when the user is inactive for too long
    const onIdle = useCallback(async () => {
        clearUiState();
        setShowWarning(false);
        setPromptRemainingSeconds(0);

        if (sessionStorage.authToken) {
            await handleLogout();
        }
    }, [handleLogout, clearUiState]);

    const onPrompt = useCallback(() => {
        // Store original title if not already stored
        if (!originalTitleRef.current) {
            originalTitleRef.current = document.title;
        }

        // Always clear any existing interval first
        if (promptIntervalRef.current) {
            clearInterval(promptIntervalRef.current);
            promptIntervalRef.current = null;
        }

        const initialSeconds = Math.ceil(promptMs / 1000) || 0;
        setShowWarning(true);
        setPromptRemainingSeconds(initialSeconds);

        // Update tab title to notify user
        document.title = "⚠️ " + (originalTitleRef.current || "App");

        // Only create interval if we don't already have one
        if (!promptIntervalRef.current) {
            promptIntervalRef.current = setInterval(() => {
                setPromptRemainingSeconds((prev) => {
                    const next = Math.max(prev - 1, 0);
                    if (next <= 0) {
                        if (promptIntervalRef.current) {
                            clearInterval(promptIntervalRef.current);
                            promptIntervalRef.current = null;
                        }
                        restoreOriginalTitle();
                    }
                    return next;
                });
            }, COUNTDOWN_INTERVAL_MS);
        }
    }, [promptMs, restoreOriginalTitle]);

    const { reset, pause, resume } = useIdleTimer({
        onIdle, // called after totalTimeoutMs of inactivity
        onAction, // called on any user activity
        onPrompt, // called promptMs before timeout
        timeout: totalTimeoutMs, // 900,000 ms (15 min)
        promptBeforeIdle: promptMs, // 540,000 ms (9 min, 3 min warning)
        debounce: debounceMs, // 60,000 ms (1 min)
    });

    const staySignedIn = useCallback(() => {
        clearUiState();
        reset();
        setShowWarning(false);
        onAction();
    }, [clearUiState, onAction, reset]);

    const onSnooze = useCallback(() => {
        // Clean up existing state
        clearUiState();
        setShowWarning(false);

        // Start snooze period (60 min)
        pause();
        renewToken(); // Initial refresh

        // Set up periodic token renewal during snooze (every 5 min)
        snoozeRenewIntervalRef.current = setInterval(() => {
            renewToken();
        }, SNOOZE_RENEW_INTERVAL_MS);

        // Resume after snooze duration (60 min)
        snoozeTimeoutRef.current = setTimeout(() => {
            clearSnoozeTimers();
            resume();
            reset(); // Give a fresh full timeout window
        }, SNOOZE_DURATION_MS);
    }, [clearUiState, pause, clearSnoozeTimers, resume, reset, renewToken]);

    const idleControlsRef = useRef(null);
    idleControlsRef.current = { pause, resume, reset, renewToken };

    // Handle auto log out when in surgery mode
    useEffect(() => {
        if (!autoLogoutPaused) {
            return; // not in surgery mode
        }

        // PAUSE idle timeout
        idleControlsRef.current.pause();
        idleControlsRef.current.renewToken();

        // Renew token every 5 min
        const renewId = setInterval(() => {
            idleControlsRef.current.renewToken();
        }, SNOOZE_RENEW_INTERVAL_MS);

        // Auto-disable surgery mode after 8 hours
        const capId = setTimeout(() => {
            dispatch(setSurgeryMode(false));
        }, SURGERY_MODE_MAX_MS);

        return () => {
            clearInterval(renewId);
            clearTimeout(capId);
            idleControlsRef.current.resume();
            idleControlsRef.current.reset();
        };
    }, [autoLogoutPaused, dispatch]);

    useEffect(() => {
        // Store the original title on mount
        originalTitleRef.current = document.title;

        const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
        logoutBroadcastChannelRef.current = channel;

        channel.onmessage = (msg) => {
            if (msg.data !== BROADCAST_CHANNEL_LOGOUT_MSG) return;

            // Do not rebroadcast here to avoid loops
            dispatchHandleLogout();
            auth0.logout({
                logoutParams: {
                    returnTo: window.location.origin + stepPath.LOGIN,
                },
            });
        };

        // Cleanup
        return () => {
            clearUiState();
            try {
                channel.close();
            } catch {
                // no-op
            }
        };
    }, [clearUiState, dispatchHandleLogout, auth0]);

    // Set up user data refresh interval
    useEffect(() => {
        // Only set up the interval if we have a username and auth token
        if (unText && authToken) {
            // Initial refresh
            refreshUserData();

            // Set up periodic refresh
            userDataRefreshIntervalRef.current = setInterval(() => {
                refreshUserData();
            }, USER_DATA_REFRESH_INTERVAL_MS);
        }

        // Clean up on unmount
        return () => {
            clearUserDataRefreshInterval();
        };
    }, [
        refreshUserData,
        unText,
        userUuid,
        authToken,
        clearUserDataRefreshInterval,
    ]);

    return (
        <div id="session-manager">
            <SessionExpiryModal
                show={showWarning}
                remainingSeconds={promptRemainingSeconds}
                onStaySignedIn={staySignedIn}
                onSnooze={onSnooze}
            />
        </div>
    );
}

SessionManager.propTypes = {
    auth0: PropTypes.object,
    timeoutDuration: PropTypes.number,
    debounceDuration: PropTypes.number,
    promptDuration: PropTypes.number,
};

SessionManager.defaultProps = {
    timeoutDuration: 15,
    debounceDuration: 1,
    promptDuration: 3,
};

export default withAuth0(SessionManager);
